import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {SMCPaths} from '../../../conf/Routes';
import {push} from 'connected-react-router';
import store from '../../../store';
import generalIcon from '../../../assets/images/general.png';
import settingsIcon from '../../../assets/images/settings.png';
import SystemRoleGeneral from './InnerPages/SystemRoleGeneral';
import SystemRoleAdvanced from './InnerPages/SystemRoleAdvanced';
import {SystemRoleGetSchema} from '../../../api/DBSchema/SystemRole';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useSystemRoleEditor(systemRoleId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origSystemRoleObject, setOrigSystemRoleObject] = useState({});
    const [systemRoleObject, setSystemRoleObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: SystemRoleGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: SystemRoleGeneral, helpId: helpContextPageUrl.roleGeneral },
        { id: SystemRoleGetSchema.pages.advanced.pageId, title: t('SMC_GEN_ADVANCED'), icon: settingsIcon, pageElement: SystemRoleAdvanced, helpId: helpContextPageUrl.roleAdvanced },
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchSystemRole = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchSystemRoleResp = await Api.fetchSingleObject(ObjectTypes.SYSTEM_ROLE, systemRoleId);
            const systemRole = fetchSystemRoleResp.data.data.role;
            // Validate server response
            if (!systemRole) {
                console.error('Get system role response failed: system role response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigSystemRoleObject(systemRole);
            setSystemRoleObject(systemRole);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [systemRoleId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setSystemRoleData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setSystemRoleObject(systemRoleObj => produce(systemRoleObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onPageChange = useCallback((pageId) => {
        setIsInnerPageLoading(true);
        setChosenPageId(pageId);
        Api.verifySingleObject(ObjectTypes.SYSTEM_ROLE, systemRoleId, systemRoleObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [systemRoleId, getMessagesArrayByPages, systemRoleObject, pageIdToPageName]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SYSTEM_ROLE, systemRoleId, systemRoleObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchSystemRole();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchSystemRole, getErrorMessage, systemRoleId, systemRoleObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [systemRoleId] ))) {
            Api.deleteObjects(ObjectTypes.SYSTEM_ROLE, [systemRoleId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.systemRoles));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [systemRoleId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [systemRoleId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setSystemRoleObject(origSystemRoleObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origSystemRoleObject]);

    // Fetch system role on loading of page
    useEffect(() => {
        fetchSystemRole();
    }, [fetchSystemRole]);
    
    return {
        systemRoleObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setSystemRoleData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchSystemRole
    };
}